import { CommonModule } from '@angular/common';
import { Component, input, ChangeDetectionStrategy, computed, inject } from '@angular/core';
import { EChartsOption } from 'echarts';
import { graphic } from 'echarts/core';
import { NgxEchartsModule } from 'ngx-echarts';
import { EchartsConfigService } from 'src/shared/services/echarts-config/echarts-config.service';

@Component({
  selector: 'mohlel-mini-line-chart',
  imports: [CommonModule, NgxEchartsModule],
  templateUrl: './mini-line-chart.component.html',
  styleUrl: './mini-line-chart.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MiniLineChartComponent {
  data = input<number[]>([]);
  color = input<'red' | 'green' | string>('green');

  private readonly echartsConfig = inject(EchartsConfigService);

  readonly chartOptions = computed<EChartsOption | undefined>(() => {
    if (!this.data()?.length) {
      return undefined;
    }
    return this.echartsConfig.mergeOptions(this.buildChartOptions());
  });

  private getColorValue(): string {
    const colorInput = this.color();
    if (colorInput === 'red') {
      return this.echartsConfig.negativeColor();
    }
    if (colorInput === 'green') {
      return this.echartsConfig.positiveColor();
    }
    return colorInput;
  }

  private buildAreaGradient(lineColor: string): graphic.LinearGradient {
    return new graphic.LinearGradient(0, 0, 0, 1, [
      { offset: 0, color: lineColor + '90' },
      { offset: 1, color: lineColor + '00' },
    ]);
  }

  private buildChartOptions(): EChartsOption {
    const lineColor = this.getColorValue();
    const dataValues = this.data();

    return {
      grid: {
        left: 2,
        right: 2,
        top: 4,
        bottom: 4,
        containLabel: false,
      },
      xAxis: {
        type: 'category',
        show: false,
        data: dataValues.map((_, index) => index),
      },
      yAxis: {
        type: 'value',
        show: false,
        scale: true,
      },
      series: [
        {
          type: 'line',
          data: dataValues,
          lineStyle: {
            color: lineColor,
            width: 1.5,
          },
          symbol: 'none',
          areaStyle: {
            color: this.buildAreaGradient(lineColor),
          },
          markPoint: {
            symbol: 'circle',
            symbolSize: 6,
            itemStyle: {
              color: lineColor,
            },
            data: [{ name: '', coord: [dataValues.length - 1, dataValues[dataValues.length - 1]] }],
          },
        },
      ],
    };
  }
}
